import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import type {
  CommissionOverviewRowsMeta,
  CommissionOverviewRowsParams,
  TargetActualRow,
  TargetActualRowsMeta,
  TargetActualRowsParams,
} from '@/lib/types';

/**
 * The documents behind one drill-down figure (owner 2026-10-28), read by the
 * drill-down modal on University Partnerships and Management -> Financial
 * Analytics. Two sources share one row shape (`TargetActualRow`):
 *
 * - `targets`: `GET /targets/actuals/rows`, the documents behind a target tile.
 * - `commission`: `GET /commissions/overview/rows`, the documents behind one
 *   person's personal or regional figure on "Targets & commission".
 *
 * - `source === null` is `idle`: nothing is fetched (the modal is closed).
 * - The request is keyed on the source JSON, so a new metric, figure, window,
 *   currency or rates basis reads as `loading` rather than showing the previous
 *   answer for a frame. Callers need not memoise the source.
 * - Every failure surfaces as an `ApiError` (a non-API failure is wrapped as
 *   code `NETWORK_ERROR`), so callers can match on `error.code`, e.g.
 *   `TOO_MANY_ROWS`.
 * - Rows and meta are passed through untouched (meta gains only `kind`):
 *   money stays decimal strings.
 * - `useTargetActualRows` is the original targets-only form, kept as a wrapper.
 */
export type DrilldownRowsSource =
  | { kind: 'targets'; params: TargetActualRowsParams }
  | { kind: 'commission'; params: CommissionOverviewRowsParams };

export type DrilldownRowsMeta =
  | ({ kind: 'targets' } & TargetActualRowsMeta)
  | ({ kind: 'commission' } & CommissionOverviewRowsMeta);

export type TargetActualRowsStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface DrilldownRowsState {
  status: TargetActualRowsStatus;
  rows: TargetActualRow[];
  meta: DrilldownRowsMeta | null;
  error: ApiError | null;
  reload: () => void;
}

export interface TargetActualRowsState {
  status: TargetActualRowsStatus;
  rows: TargetActualRow[];
  meta: TargetActualRowsMeta | null;
  error: ApiError | null;
  reload: () => void;
}

interface Answer {
  key: string;
  rows: TargetActualRow[];
  meta: DrilldownRowsMeta | null;
  error: ApiError | null;
}

const NO_ROWS: TargetActualRow[] = [];

function asApiError(caught: unknown): ApiError {
  if (caught instanceof ApiError) return caught;
  const message =
    caught instanceof Error && caught.message !== '' ? caught.message : 'Could not read the documents behind this figure.';
  return new ApiError(0, { error: { code: 'NETWORK_ERROR', message, requestId: '' } });
}

function fetchRows(
  source: DrilldownRowsSource,
  signal: AbortSignal,
): Promise<{ rows: TargetActualRow[]; meta: DrilldownRowsMeta }> {
  if (source.kind === 'commission') {
    return api
      .commissionOverviewRows(source.params, signal)
      .then((response) => ({ rows: response.data, meta: { kind: 'commission' as const, ...response.meta } }));
  }
  return api
    .targetActualRows(source.params, signal)
    .then((response) => ({ rows: response.data, meta: { kind: 'targets' as const, ...response.meta } }));
}

export function useDrilldownRows(source: DrilldownRowsSource | null): DrilldownRowsState {
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  const sourceKey = source === null ? null : JSON.stringify(source);
  const key = sourceKey === null ? null : `${reloadToken}|${sourceKey}`;

  useEffect(() => {
    if (sourceKey === null) return;
    const controller = new AbortController();
    const { signal } = controller;
    const stamp = `${reloadToken}|${sourceKey}`;
    const request = JSON.parse(sourceKey) as DrilldownRowsSource;

    fetchRows(request, signal)
      .then(({ rows, meta }) => {
        if (signal.aborted) return;
        setAnswer({ key: stamp, rows, meta, error: null });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setAnswer({ key: stamp, rows: NO_ROWS, meta: null, error: asApiError(caught) });
      });

    return () => controller.abort();
  }, [sourceKey, reloadToken]);

  if (key === null) return { status: 'idle', rows: NO_ROWS, meta: null, error: null, reload };
  const current = answer !== null && answer.key === key ? answer : null;
  if (current === null) return { status: 'loading', rows: NO_ROWS, meta: null, error: null, reload };
  if (current.error !== null) return { status: 'error', rows: NO_ROWS, meta: null, error: current.error, reload };
  return { status: 'ready', rows: current.rows, meta: current.meta, error: null, reload };
}

export function useTargetActualRows(params: TargetActualRowsParams | null): TargetActualRowsState {
  const state = useDrilldownRows(params === null ? null : { kind: 'targets', params });
  const meta = state.meta !== null && state.meta.kind === 'targets' ? state.meta : null;
  return { ...state, meta };
}
